import React, { useState, useEffect } from 'react'
import {
    Box,
    Flex,
    Icon,
    SimpleGrid,
    FormControl,
    FormLabel,
    Input,
    useColorModeValue,
    Spinner,
    Select,
    Text
} from "@chakra-ui/react"

import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import MiniStatistics from "components/card/MiniStatistics"
import IconBox from "components/icons/IconBox"
import CheckTable from "views/admin/default/components/CheckTable"
import {
    MdAttachMoney,
    MdBarChart,
    MdFileCopy, MdSwapHoriz, MdTrendingUp
} from "react-icons/md"
import axiosInstance from "../../../api/axiosInstance"
import { formatNumber } from "../../../Utils/Utils"
import dayjs from "dayjs"
import { formatInTimeZone } from 'date-fns-tz'
import DailyTraffic from "./DailyTraffic";
import TotalSpent from "./TotalSpent";
import TrafficByProduct from "./TrafficByProduct";
import PieCard from "./PieCard";
import BestSellingOptionsChart from "./BestSellingOption";
import {createColumnHelper} from "@tanstack/react-table";
import UpsellsList from "./components/UpsellsList";
import isoWeek from 'dayjs/plugin/isoWeek'

const Dashboard = () => {
    const [loading, setLoading] = useState(true)
    const [metrics, setMetrics] = useState({})
    const [startDate, setStartDate] = useState(dayjs())
    const [endDate, setEndDate] = useState(dayjs())
    const [period, setPeriod] = useState('yesterday')
    const [selectedRange, setSelectedRange] = useState('Today')
    const [roasLoading, setRoasLoading] = useState(false)
    const timeZone = 'Europe/Bucharest'
    dayjs.extend(isoWeek)

    useEffect(() => {
        updateDateRange(selectedRange)
    }, [])

    useEffect(() => {
        fetchMetrics(startDate, endDate)
    }, [startDate, endDate])

    const fetchMetrics = async (start, end) => {
        setLoading(true)
        try {
            const formattedStartDate = start ? formatInTimeZone(start.toDate(), timeZone, 'yyyy-MM-dd') : undefined
            const formattedEndDate = end ? formatInTimeZone(end.toDate(), timeZone, 'yyyy-MM-dd') : undefined
            const response = await axiosInstance.get('/reports/metrics', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: {
                    start_date: formattedStartDate,
                    end_date: formattedEndDate,
                },
            })
            setMetrics(response.data)
        } catch (error) {
            console.error('Error fetching metrics:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleStartDateChange = (newDate) => {
        setStartDate(dayjs(newDate))
        if (selectedRange !== 'Custom') {
            setSelectedRange('Custom')
        }
    }

    const handleEndDateChange = (newDate) => {
        setEndDate(dayjs(newDate))
        if (selectedRange !== 'Custom') {
            setSelectedRange('Custom')
        }
    }

    const updateDateRange = (range) => {
        setSelectedRange(range)
        switch (range) {
            case 'Today':
                setStartDate(dayjs().startOf('day'))
                setEndDate(dayjs().endOf('day'))
                setPeriod('yesterday')
                break
            case 'Yesterday':
                setStartDate(dayjs().subtract(1, 'day').startOf('day'))
                setEndDate(dayjs().subtract(1, 'day').endOf('day'))
                setPeriod('the day before yesterday')
                break
            case 'This week':
                setStartDate(dayjs().startOf('isoWeek'))
                setEndDate(dayjs().endOf('isoWeek'))
                setPeriod('last week')
                break
            case 'Last week':
                setStartDate(dayjs().subtract(1, 'week').startOf('isoWeek'))
                setEndDate(dayjs().subtract(1, 'week').endOf('isoWeek'))
                setPeriod('the week before last week')
                break
            case 'This month':
                setStartDate(dayjs().startOf('month'))
                setEndDate(dayjs().endOf('month'))
                setPeriod('last month')
                break
            case 'Last month':
                setStartDate(dayjs().subtract(1, 'month').startOf('month'))
                setEndDate(dayjs().subtract(1, 'month').endOf('month'))
                setPeriod('the month before last month')
                break
            case 'This year':
                setStartDate(dayjs().startOf('year'))
                setEndDate(dayjs().endOf('year'))
                setPeriod('last year')
                break
            case 'Last year':
                setStartDate(dayjs().subtract(1, 'year').startOf('year'))
                setEndDate(dayjs().subtract(1, 'year').endOf('year'))
                setPeriod('the year before last year')
                break
            case 'Maximum':
                setStartDate(null)
                setEndDate(null)
                setPeriod(null)
                break
            default:
                setStartDate(dayjs().startOf('day'))
                setEndDate(dayjs().endOf('day'))
        }
    }

    const calculatePercentageChange = (current, previous) => {
        if (!previous) return 0
        return ((current - previous) / previous) * 100
    }

    const brandColor = useColorModeValue("brand.500", "white")
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100")

    const formattedProductSales = metrics.productSales?.map(product => ({
        name: [product.name, false],
        totalSales: `${formatNumber(product.totalSales)} RON`,
        quantity: formatNumber(product.orderCount),
        unitsSold: product.unitsSold,
        returnRate: product.returnRate != null
            ? `${formatNumber(product.returnRate)}%`
            : '-',
    }))

    const fetchRoasDetails = async () => {
        setRoasLoading(true)
        try {
            const response = await axiosInstance.get('/roas/details', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })

            fetchMetrics(startDate, endDate)
        } catch (error) {
            console.error('Error fetching ROAS details:', error)
        } finally {
            setRoasLoading(false)
        }
    }

    const ch = createColumnHelper();

    const upsellColumns = [
        ch.accessor('upsell_name', {
            id: 'upsell_name',
            header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">UPSELL</Text>,
            cell: (info) => <Text fontWeight="700">{info.getValue()}</Text>,
        }),
        ch.accessor('order_count', {
            id: 'order_count',
            header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">ORDERS</Text>,
            cell: (info) => <Text fontWeight="700">{info.getValue()}</Text>,
        }),
    ];

    if (loading) {
        return (
            <Flex justify="center" align="center" height="100vh">
                <Spinner size="xl" />
            </Flex>
        )
    }

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxWidth="xxl" px={{ base: '20px', md: '40px' }}>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                gap="20px"
                mb="20px"
            >
                <FormControl>
                    <FormLabel>Start Date</FormLabel>
                    <DatePicker
                        selected={startDate?.toDate()}
                        onChange={handleStartDateChange}
                        customInput={<Input />}
                        dateFormat="dd-MM-yyyy"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>End Date</FormLabel>
                    <DatePicker
                        selected={endDate?.toDate()}
                        onChange={handleEndDateChange}
                        customInput={<Input />}
                        dateFormat="dd-MM-yyyy"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Date Range</FormLabel>
                    <Select value={selectedRange} onChange={(e) => updateDateRange(e.target.value)}>
                        <option value="Today">Today</option>
                        <option value="Yesterday">Yesterday</option>
                        <option value="This week">This Week</option>
                        <option value="Last week">Last Week</option>
                        <option value="This month">This Month</option>
                        <option value="Last month">Last Month</option>
                        <option value="This year">This Year</option>
                        <option value="Last year">Last Year</option>
                        <option value="Maximum">Maximum</option>
                    </Select>
                </FormControl>
            </SimpleGrid>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                gap="20px"
                mb="20px"
            >
                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdBarChart}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name="Total Sales / Orders"
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.totalSalesAll)} RON / {formatNumber(metrics.totalOrdersAll)} orders
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSalesAll,
                        metrics.previousTotalSalesAll
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdFileCopy}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name="Total / Orders Delivered"
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.totalSales)} RON / {formatNumber(metrics.orderCount)} orders
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSales,
                        metrics.previousTotalSales
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdAttachMoney}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name="Average Order Value"
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.averageOrderValue)} RON
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.averageOrderValue,
                        metrics.previousAverageOrderValue
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdAttachMoney}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name="Spend"
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.totalSpend)} RON
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSpend,
                        metrics.previousTotalSpend
                    )}
                />

                <div onDoubleClick={fetchRoasDetails}>
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w={{ base: "32px", md: "40px" }}
                                h={{ base: "32px", md: "40px" }}
                                bg={roasLoading ? "gray.200" : boxBg}
                                borderRadius="12px"
                                boxShadow="sm"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                icon={
                                    roasLoading ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Icon
                                            as={MdTrendingUp}
                                            w={{ base: "18px", md: "24px" }}
                                            h={{ base: "18px", md: "24px" }}
                                            color={brandColor}
                                        />
                                    )
                                }
                            />
                        }
                        period={period}
                        name="ROAS"
                        value={
                            <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                                {formatNumber(metrics.roas)}
                            </Box>
                        }
                        roasTime={metrics.last_roas_time}
                        growth={calculatePercentageChange(
                            metrics.roas,
                            metrics.previousRoas
                        )}
                    />
                </div>

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdSwapHoriz}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name="Conversion Rate"
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.conversionRate)}%
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.conversionRate,
                        metrics.previousConversionRate
                    )}
                />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <CheckTable
                    columnsData={[
                        { Header: "Product Name", accessor: "name" },
                        { Header: "Total Sales", accessor: "totalSales" },
                        { Header: "Total Orders", accessor: "orderCount" },
                        { Header: "Total Units Sold", accessor: "unitsSold" },
                        { Header: "Return %", accessor: "returnRate" },
                    ]}
                    tableData={formattedProductSales || []}
                />
                <DailyTraffic />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <TotalSpent/>
                <TrafficByProduct productTraffic={metrics.productTraffic || []} />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <PieCard data={metrics.orderSummary || []}/>
                <BestSellingOptionsChart productSales={metrics.productSales || []}/>
            </SimpleGrid>

            {
                Array.isArray(metrics?.upsells) && metrics.upsells.length > 0 &&
                <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                    <UpsellsList
                        data={metrics.upsells || []}
                        title="Upsells"
                        maxHeight="420px"
                    />
                </SimpleGrid>
            }
        </Box>
    )
}

export default Dashboard
